import { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sportscanner/ui";
import { clubInfoSchema } from "@sportscanner/schemas";
import { apiPost, getApiErrorCode } from "@/lib/api";
import { isPhoneValid } from "@/lib/phone";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import PhoneInput from "@/components/booking/phone-input";
import LocationPicker, {
  type PickedLocation,
} from "@/components/location-picker";
import type { MeClub, MeSport } from "@/lib/me";
import { SPORT_KEYS, type SportKey } from "@/lib/sports";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;

interface SportRow {
  selected: boolean;
  durationInput: string;
}

type SportsState = Record<SportKey, SportRow>;

function initialSports(clubSports: MeSport[]): SportsState {
  const byName = new Map(clubSports.map((s) => [s.name, s]));
  return SPORT_KEYS.reduce((acc, k) => {
    const existing = byName.get(k);
    acc[k] = {
      selected: !!existing,
      durationInput: existing ? String(existing.sessionDurationMinutes) : "60",
    };
    return acc;
  }, {} as SportsState);
}

function parseDuration(input: string): number {
  const n = input === "" ? NaN : Number(input);
  return Number.isFinite(n) ? n : NaN;
}

interface Props {
  club: MeClub;
  onDone: (sports: MeSport[]) => void;
}

export default function ClubInfoStep({ club, onDone }: Props) {
  const { t } = useTranslation();

  const [address, setAddress] = useState(club.address || "");
  const [city, setCity] = useState(club.city || "");
  const [country, setCountry] = useState(club.country || DEFAULT_COUNTRY);
  const [lat, setLat] = useState<number | null>(club.lat ?? null);
  const [lng, setLng] = useState<number | null>(club.lng ?? null);
  const [phone, setPhone] = useState(club.phone || "");
  const [workingHoursStart, setWorkingHoursStart] = useState(
    club.workingHoursStart || "08:00",
  );
  const [workingHoursEnd, setWorkingHoursEnd] = useState(
    club.workingHoursEnd || "22:00",
  );
  const [bookingDaysInput, setBookingDaysInput] = useState(
    String(club.bookingDaysAdvance ?? 14),
  );
  const [sports, setSports] = useState<SportsState>(() =>
    initialSports(club.sports),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const selectedSports = SPORT_KEYS.filter((k) => sports[k].selected);
  const bookingDaysNumber =
    bookingDaysInput === "" ? NaN : Number(bookingDaysInput);
  const bookingDaysValid =
    Number.isInteger(bookingDaysNumber) &&
    bookingDaysNumber >= 1 &&
    bookingDaysNumber <= 90;
  const phoneInvalid = phone.trim().length > 0 && !isPhoneValid(phone);
  const isValid =
    address.trim().length >= 3 &&
    city.trim().length >= 1 &&
    country.length === 2 &&
    isPhoneValid(phone) &&
    workingHoursStart < workingHoursEnd &&
    bookingDaysValid &&
    selectedSports.length >= 1 &&
    selectedSports.every((k) => {
      const m = parseDuration(sports[k].durationInput);
      return Number.isInteger(m) && m >= 15 && m <= 240;
    });

  function toggleSport(key: SportKey) {
    setSports((s) => ({
      ...s,
      [key]: { ...s[key], selected: !s[key].selected },
    }));
  }

  function updateDuration(key: SportKey, value: string) {
    setSports((s) => ({
      ...s,
      [key]: { ...s[key], durationInput: value },
    }));
  }

  function handleLocationChange(loc: PickedLocation) {
    setLat(loc.lat);
    setLng(loc.lng);
    if (loc.address) setAddress(loc.address);
    if (loc.city) setCity(loc.city);
    if (loc.country) setCountry(loc.country.toUpperCase());
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    const parsed = clubInfoSchema.safeParse({
      address: address.trim(),
      city: city.trim(),
      country,
      lat,
      lng,
      phone: phone.trim(),
      workingHoursStart,
      workingHoursEnd,
      bookingDaysAdvance: bookingDaysNumber,
      sports: selectedSports.map((k) => ({
        name: k,
        sessionDurationMinutes: parseDuration(sports[k].durationInput),
      })),
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsSubmitting(true);
    setErrorCode(null);

    try {
      const result = await apiPost<{ ok: true; sports: MeSport[] }>(
        "/api/admin/onboarding/club-info",
        parsed.data,
      );
      onDone(result.sports);
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-6"
    >
      {errorCode && errorCode !== "PHONE_INVALID" && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      {/* Location: search the map → fills address/city/country + sets the pin */}
      <LocationPicker
        apiKey={GOOGLE_MAPS_KEY}
        lat={lat}
        lng={lng}
        disabled={isSubmitting}
        onChange={handleLocationChange}
      />

      {/* Address (street only) */}
      <div className="space-y-2">
        <Label htmlFor="address" className="text-sm text-neutral-700">
          {t("onboarding.address.label")}{" "}
          <span className="text-red-500">*</span>
        </Label>
        <Input
          id="address"
          type="text"
          placeholder={t("onboarding.address.placeholder")}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={isSubmitting}
          className="rounded-lg border-neutral-200"
        />
        <p className="text-xs text-neutral-500">
          {t("onboarding.address.help")}
        </p>
      </div>

      {/* City + Country */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city" className="text-sm text-neutral-700">
            {t("onboarding.city.label")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="city"
            type="text"
            placeholder={t("onboarding.city.placeholder")}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={isSubmitting}
            className="rounded-lg border-neutral-200"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-neutral-700">
            {t("onboarding.country.label")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Select
            value={country}
            onValueChange={setCountry}
            disabled={isSubmitting}
          >
            <SelectTrigger className="rounded-lg border-neutral-200 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm text-neutral-700">
          {t("onboarding.phone.label")} <span className="text-red-500">*</span>
        </Label>
        <PhoneInput
          id="phone"
          value={phone}
          onChange={setPhone}
          disabled={isSubmitting}
          invalid={phoneInvalid || errorCode === "PHONE_INVALID"}
        />
        {phoneInvalid || errorCode === "PHONE_INVALID" ? (
          <p className="text-xs text-red-500">
            {t("errors.api.PHONE_INVALID")}
          </p>
        ) : (
          <p className="text-xs text-neutral-500">
            {t("onboarding.phone.help")}
          </p>
        )}
      </div>

      {/* Working hours */}
      <div className="space-y-2">
        <Label className="text-sm text-neutral-700">
          {t("onboarding.workingHours.label")}{" "}
          <span className="text-red-500">*</span>
        </Label>
        <div className="flex items-center gap-2 sm:gap-3">
          <Input
            type="time"
            value={workingHoursStart}
            onChange={(e) => setWorkingHoursStart(e.target.value)}
            disabled={isSubmitting}
            className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
          />
          <span className="text-neutral-400">—</span>
          <Input
            type="time"
            value={workingHoursEnd}
            onChange={(e) => setWorkingHoursEnd(e.target.value)}
            disabled={isSubmitting}
            className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
          />
        </div>
        <p className="text-xs text-neutral-500">
          {t("onboarding.workingHours.help")}
        </p>
      </div>

      {/* Booking days advance */}
      <div className="space-y-2">
        <Label htmlFor="bookingDays" className="text-sm text-neutral-700">
          {t("onboarding.bookingDays.label")}{" "}
          <span className="text-red-500">*</span>
        </Label>
        <Input
          id="bookingDays"
          type="number"
          inputMode="numeric"
          min={1}
          max={90}
          placeholder="14"
          value={bookingDaysInput}
          onChange={(e) => setBookingDaysInput(e.target.value)}
          disabled={isSubmitting}
          className="rounded-lg border-neutral-200 w-32"
        />
        <p className="text-xs text-neutral-500">
          {t("onboarding.bookingDays.help")}
        </p>
      </div>

      {/* Sports */}
      <div className="space-y-3">
        <Label className="text-sm text-neutral-700">
          {t("onboarding.sports.label")} <span className="text-red-500">*</span>
        </Label>
        <p className="text-xs text-neutral-500">
          {t("onboarding.sports.help")}
        </p>
        <div className="space-y-2">
          {SPORT_KEYS.map((key) => (
            <div
              key={key}
              className={`rounded-lg border p-3 transition-colors ${
                sports[key].selected
                  ? "border-primary-300 bg-primary-50/40"
                  : "border-neutral-200"
              }`}
            >
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={sports[key].selected}
                  onCheckedChange={() => toggleSport(key)}
                  disabled={isSubmitting}
                />
                <span className="text-sm font-medium text-neutral-800 flex-1">
                  {t(`sports.${key}`)}
                </span>
              </label>
              {sports[key].selected && (
                <div className="mt-3 ml-7 flex items-center gap-2">
                  <Label
                    htmlFor={`duration-${key}`}
                    className="text-xs text-neutral-600"
                  >
                    {t("onboarding.sports.durationLabel")}
                  </Label>
                  <Input
                    id={`duration-${key}`}
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={240}
                    placeholder="60"
                    value={sports[key].durationInput}
                    onChange={(e) => updateDuration(key, e.target.value)}
                    disabled={isSubmitting}
                    className="rounded-lg border-neutral-200 w-24 h-8"
                  />
                  <span className="text-xs text-neutral-500">
                    {t("onboarding.sports.minutes")}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Submit */}
      <Button
        type="submit"
        disabled={!isValid || isSubmitting}
        className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("onboarding.submitting")}
          </>
        ) : (
          t("onboarding.next")
        )}
      </Button>
    </form>
  );
}
